// src/lib/data-api.js — LE BLOB DE FACTURATION PASSE PAR LE SERVEUR.
//
// ══ POURQUOI (décision Dimitri, 2026-09-08) ═════════════════════════════════
//
//   « Deux onglets ouverts et je perds ma saisie de facturation sans le voir —
//     c'est de l'argent, et le remède existe déjà à côté. »
//
// Jusqu'ici `saveData` écrivait `fbz8-data` directement en base depuis le
// navigateur : le blob ENTIER (607 Ko mesurés le 2026-09-08), dernier arrivé
// gagne, sans notion de version. Ce module le fait passer par `/api/data`, qui
// refuse en 409 si la base a bougé depuis le chargement de la page.
//
// ⚠️ MIROIR EXACT de src/lib/cfg-api.js, y compris son piège le plus subtil
// (voir `saveDataRemote`). Le mécanisme est le même parce que le problème est
// le même : on ne réinvente pas une garde qui fonctionne à côté.

import { supabase } from "../supabase";
import { lireJsonOuRefuser, ReponseIllisible } from "./reponse-json";

// La version détenue par CET onglet. C'est un état de module, et c'est
// délibéré : deux onglets sont deux contextes JavaScript distincts, donc deux
// jetons distincts — ce qui est exactement ce qui permet de les départager.
let jetonVersion = "";

// F13 — nombre d'écritures parties et pas encore revenues. Tant qu'une
// écriture est en vol, une relecture peut décrire un état ANTÉRIEUR à elle :
// ni la relecture ni un import ne doivent alors toucher à l'écran ou écrire.
let ecrituresEnVol = 0;

// F13 — le dernier geste d'enregistrement de Dimitri a été REFUSÉ (409, 503,
// réseau) et sa saisie est restée à l'écran. La relecture qui remettra l'écran
// sur la base doit le DIRE, une fois (cf. `appliquerRelecture`).
let saisieRefusee = false;
// Le dernier geste a échoué SANS refus certain (réseau, 500, illisible,
// délai) : on ne sait pas s'il est passé. Une relecture au MÊME jeton prouve
// qu'il ne l'est pas (tour 3, point 3).
let gesteIncertain = false;
// L'issue du DERNIER geste qui a échoué : vrai si on ne sait pas s'il est
// passé (réseau, délai, 500, illisible). Lu par la fenêtre CSV pour choisir
// entre « réessayez » et « je ne sais pas » (tour 4, R3). Remis à faux au
// début de chaque geste (`debutGeste`, appelé par saveData).
let echecIncertainDernier = false;

/** Début d'un geste d'enregistrement : l'issue du précédent ne vaut plus. */
export function debutGeste() {
  echecIncertainDernier = false;
}

/** Le dernier geste a-t-il échoué SANS qu'on sache s'il est passé ? */
export function dernierEchecIncertain() {
  return echecIncertainDernier;
}

/** Lecture — montrée en bandeau (cf. src/lib/reponse-json.js). */
export const MESSAGE_DATA_ILLISIBLE =
  "Je n'ai pas pu lire tes données de facturation : ce que le serveur a " +
  "renvoyé n'en était pas. Les montants peuvent s'afficher vides ou " +
  "incomplets — rien n'est perdu, recharge la page.";

/**
 * Lecture refusée par le serveur parce qu'IL n'a pas pu relire la base (503,
 * F13). Texte décidé par Dimitri le 2026-10-02 — montré dans le bandeau de
 * lecture, à la place d'un écran de facturation vide.
 */
export const MESSAGE_DATA_LECTURE_IMPOSSIBLE =
  "Lecture impossible pour le moment — vos données ne sont pas perdues. " +
  "Réessayez dans un instant.";

/**
 * Un rafraîchissement a trouvé des données plus récentes, mais une saisie est
 * en cours : on ne les adopte pas sous les doigts de Dimitri. Texte décidé
 * par Dimitri le 2026-10-02 — aucun autre.
 */
export const MESSAGE_DATA_CHANGEES_AILLEURS =
  "Des changements ont eu lieu ailleurs — rechargez avant d'enregistrer";

/**
 * Un refus d'enregistrement a laissé une saisie à l'écran, et une relecture
 * vient de remettre l'écran sur la base. Texte demandé le 2026-10-02 — À FAIRE
 * VALIDER PAR DIMITRI.
 */
export const MESSAGE_DATA_SAISIE_NON_ENREGISTREE =
  "Votre dernière modification n'a pas été enregistrée ; l'écran a été remis à l'état réel.";

/** Écriture — on ne peut pas dire si c'est passé. C'est de l'argent : ne pas mentir. */
export const MESSAGE_DATA_ECRITURE_ILLISIBLE =
  "Je n'ai pas pu lire la réponse du serveur : je ne sais pas si " +
  "l'enregistrement est passé. Ne réessaie pas à l'aveugle — recharge la " +
  "page pour voir ce qui est vraiment enregistré.";

/**
 * F13 — un enregistrement qui ne revient pas en 60 s : on ne sait pas s'il est
 * passé, et on le dit (texte demandé le 2026-10-02, à faire valider par Dimitri).
 */
export const DELAI_ECRITURE_MS = 60000;
export const MESSAGE_DATA_ECRITURE_DELAI =
  "Je ne sais pas si l'enregistrement est passé — rechargez pour vérifier.";

async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Le verdict « ce n'est pas du JSON » vit dans src/lib/reponse-json.js —
// quatre recopies de cette fonction laissaient passer une page HTML en 200
// pour un blob vide, et vidaient les écrans sans un mot.
async function parseOrThrow(res, fallback, messageIllisible) {
  return lireJsonOuRefuser(res, fallback, messageIllisible);
}

/**
 * Lit le blob et rend `{ data, jeton }` — SANS toucher au jeton détenu.
 *
 * ══ POURQUOI ELLE NE MÉMORISE PLUS RIEN (F13, 2026-10-02) ═══════════════════
 *
 * Avant, la lecture REMPLAÇAIT le jeton détenu. Le rafraîchissement iCal relit
 * le blob toutes les cinq minutes ; quand l'iCal n'ajoutait rien, l'écran
 * gardait ses anciennes données… mais recevait le jeton FRAIS. Un onglet resté
 * ouvert devenait ainsi « à jour » aux yeux du serveur sans l'être à l'écran :
 * son prochain enregistrement passait, et écrasait en silence un geste fait
 * ailleurs (l'opérateur, un autre onglet). Le jeton était blanchi.
 *
 * Désormais l'appelant ADOPTE les deux ensemble — données à l'écran et jeton —
 * avec `adopterJeton`, ou n'adopte AUCUN des deux. Un jeton ne voyage plus
 * jamais sans les données qu'il décrit.
 */
export async function fetchData() {
  const res = await fetch("/api/data", { headers: await authHeaders() });
  // 503 : le serveur n'a pas pu relire la base. Ce n'est pas un blob vide —
  // on refuse, avec la phrase de Dimitri, et le bandeau de lecture la montre.
  if (res.status === 503)
    throw new ReponseIllisible(MESSAGE_DATA_LECTURE_IMPOSSIBLE);
  const data = await parseOrThrow(
    res,
    "Lecture des données échouée",
    MESSAGE_DATA_ILLISIBLE,
  );
  return { data, jeton: res.headers?.get?.("x-data-version") || "" };
}

/**
 * Adopter le jeton d'une lecture — À APPELER EN MÊME TEMPS que l'adoption de
 * ses données à l'écran, jamais seul.
 */
export function adopterJeton(jeton) {
  jetonVersion = typeof jeton === "string" ? jeton : "";
}

/** Le jeton que CET onglet détient (celui qui partira au prochain enregistrement). */
export function jetonDetenu() {
  return jetonVersion;
}

/**
 * Fin de session (déconnexion) : le jeton et les signaux de geste de la
 * session d'avant ne valent plus rien. Sans cela, après une reconnexion dans
 * le même onglet, l'ancien jeton passait pour une lecture réussie (relecture
 * adversariale F13 suite, tour 3).
 */
// Limite connue (REGLES 76, pas de compteur de session) : une écriture en vol au moment de la déconnexion peut reposer un jeton après cet oubli — sans doublon possible, la garde de version du serveur refuse tout état périmé.
export function oublierSession() {
  jetonVersion = "";
  saisieRefusee = false;
  gesteIncertain = false;
}

/**
 * Les messages du chargement de session, moins ceux de la lecture des
 * données QUAND une relecture a adopté données et jeton PENDANT ce chargement
 * (le jeton détenu a changé depuis `jetonDebut`) : la donnée est lue, la
 * panne du chargement est périmée. Un jeton déjà détenu AU DÉBUT (session
 * d'avant, chargement précédent) ne prouve rien.
 *
 * @param {{messages: string[], chargementRate: boolean, jetonDebut: string}} p
 */
export function pannesDuChargement({ messages, chargementRate, jetonDebut }) {
  const liste = Array.isArray(messages) ? messages : [];
  const relueEntreTemps =
    chargementRate &&
    jetonVersion !== "" &&
    jetonVersion !== (jetonDebut || "");
  if (!relueEntreTemps) return liste;
  return liste.filter(
    (m) =>
      m !== MESSAGE_DATA_LECTURE_IMPOSSIBLE && m !== MESSAGE_DATA_ILLISIBLE,
  );
}

/** Repère posé sur la zone de l'écran Argent (Données et Facturation). */
export const ATTR_ZONE_ARGENT = "data-zone-argent";
/** Repère posé sur une fenêtre de saisie de l'écran Argent tenue par un
 *  sous-composant (aujourd'hui : l'import CSV), invisible pour App. */
export const ATTR_SAISIE_ARGENT = "data-saisie-argent";

/**
 * Une saisie est-elle en cours SUR L'ÉCRAN ARGENT ? Trois signaux :
 *   · une fenêtre d'édition tenue par App est ouverte (réservation, prorata,
 *     extra, ajustement, confirmation) ;
 *   · une fenêtre marquée `data-saisie-argent` est présente (import CSV) ;
 *   · un champ a le curseur ET se trouve DANS la zone `data-zone-argent`.
 * La boîte de discussion de l'opérateur, une recherche, un champ d'un autre
 * écran ne comptent pas : ils ne portent pas de données de facturation.
 *
 * @param {{modaleOuverte: boolean, elementActif?: Element|null,
 *          racine?: {querySelector: Function}|null}} p
 */
export function saisieEnCours({ modaleOuverte, elementActif, racine } = {}) {
  if (modaleOuverte) return true;
  if (
    racine &&
    typeof racine.querySelector === "function" &&
    racine.querySelector(`[${ATTR_SAISIE_ARGENT}]`)
  )
    return true;
  const el = elementActif;
  if (!el) return false;
  const dansZone =
    typeof el.closest === "function" && !!el.closest(`[${ATTR_ZONE_ARGENT}]`);
  if (!dansZone) return false;
  if (el.isContentEditable) return true;
  const balise = String(el.tagName || "").toUpperCase();
  return balise === "INPUT" || balise === "TEXTAREA" || balise === "SELECT";
}

/**
 * Que faire d'une relecture ? Décision de Dimitri (2026-10-02) :
 *   · même jeton que celui détenu → rien n'a bougé ailleurs : 'identique' ;
 *   · jeton différent et rien en cours de saisie → l'écran recharge seul :
 *     'adopter' (données ET jeton, ensemble) ;
 *   · jeton différent et une saisie en cours → 'bandeau' : on n'adopte RIEN,
 *     on garde l'ancien jeton (l'enregistrement sera refusé en 409, comme
 *     avant) et on prévient.
 */
export function decisionRelecture({ jetonLu, jetonDetenu: detenu, enSaisie }) {
  if ((jetonLu || "") === (detenu || "")) return "identique";
  return enSaisie ? "bandeau" : "adopter";
}

/**
 * Appliquer une relecture du rafraîchissement — la décision ET ses effets, en
 * un seul endroit testable.
 *
 * @param {object} p
 * @param {string} p.jetonAvant      le jeton détenu noté AVANT `fetchData` :
 *   s'il a changé pendant la relecture (un enregistrement est passé entre-
 *   temps), la relecture est abandonnée — rien n'est adopté.
 * @param {string} p.jetonLu         le jeton rendu par `fetchData`
 * @param {object} p.donnees         les données lues (déjà migrées)
 * @param {boolean} p.enSaisie       cf. `saisieEnCours`
 * @param {Function} p.adopterDonnees (donnees) => void — met l'écran à jour
 * @param {Function} p.montrerBandeau (message) => void
 * @param {Function} p.retirerBandeau (message) => void
 * @returns {boolean} `true` si le rafraîchissement peut continuer (import
 *   iCal) ; `false` s'il doit s'arrêter là (bandeau posé, rien adopté).
 */
export function appliquerRelecture({
  jetonAvant,
  jetonLu,
  donnees,
  enSaisie,
  adopterDonnees,
  montrerBandeau,
  retirerBandeau,
}) {
  // Un enregistrement a abouti PENDANT la relecture : ce qu'on a lu peut être
  // antérieur à lui. On n'adopte rien, on n'importe rien ; le prochain
  // rafraîchissement relira.
  if (jetonVersion !== (jetonAvant || "")) return false;
  // Une écriture est encore en vol (Dimitri vient d'enregistrer) : ce qu'on
  // a lu peut lui être antérieur. Adopter remplacerait sa saisie à l'écran
  // par l'état d'avant, que son geste suivant réécrirait en base.
  if (ecrituresEnVol > 0) return false;
  const decision = decisionRelecture({
    jetonLu,
    jetonDetenu: jetonVersion,
    enSaisie,
  });
  if (decision === "bandeau") {
    montrerBandeau(MESSAGE_DATA_CHANGEES_AILLEURS);
    return false;
  }
  if (decision === "adopter") {
    // Les DEUX ensemble : l'écran reçoit les données que le jeton décrit.
    adopterDonnees(donnees);
    adopterJeton(jetonLu);
    // La donnée est lue : les avertissements de lecture n'ont plus d'objet.
    retirerBandeau(MESSAGE_DATA_LECTURE_IMPOSSIBLE);
    retirerBandeau(MESSAGE_DATA_ILLISIBLE);
    retirerBandeau(MESSAGE_DATA_CHANGEES_AILLEURS);
    direSaisieEffacee(montrerBandeau);
    return true;
  }
  // 'identique' — même jeton. ⚠️ « même jeton » ne veut PAS dire « l'écran
  // montre la base » : un import refusé, un enregistrement concurrent ont pu
  // laisser à l'écran autre chose que ce que ce jeton décrit (équipe rouge,
  // 2026-10-02). Sans saisie en cours, l'écran REPREND donc les données
  // relues, et alors seulement le bandeau est retiré. Avec une saisie en
  // cours, on ne touche ni à l'écran ni au bandeau.
  if (!enSaisie) {
    adopterDonnees(donnees);
    // La donnée est lue et adoptée : les avertissements de lecture n'ont plus
    // d'objet (sinon « Lecture impossible » remis par un chargement raté
    // restait collé à vie — relecture adversariale F13 suite, tour 2).
    retirerBandeau(MESSAGE_DATA_LECTURE_IMPOSSIBLE);
    retirerBandeau(MESSAGE_DATA_ILLISIBLE);
    retirerBandeau(MESSAGE_DATA_CHANGEES_AILLEURS);
    direSaisieEffacee(montrerBandeau, { memeJeton: true });
    return true;
  }
  // Saisie en cours (F13 suite, point 5) : l'import NE part PAS — il écrirait
  // pendant que Dimitri saisit. Il reprendra au passage suivant. Pas de
  // bandeau : rien n'a bougé ailleurs.
  return false;
}

/**
 * L'écran vient d'être remis sur la base : si le dernier geste de Dimitri
 * avait été refusé, sa saisie vient de disparaître — on le dit, une fois.
 */
function direSaisieEffacee(montrerBandeau, { memeJeton = false } = {}) {
  // Tour 3, point 3 : un geste INCERTAIN suivi d'une relecture au MÊME jeton
  // n'est pas passé (sinon la base, donc le jeton, aurait changé) — on peut
  // alors le dire. Sur un jeton nouveau, on ne sait toujours pas : on oublie.
  const aDire = saisieRefusee || (memeJeton && gesteIncertain);
  gesteIncertain = false;
  if (!aDire || typeof montrerBandeau !== "function") return;
  saisieRefusee = false;
  montrerBandeau(MESSAGE_DATA_SAISIE_NON_ENREGISTREE);
}

/**
 * Un refus CERTAIN (relecture argent F13 suite) : seul un refus EXPLICITE du
 * serveur, rendu AVANT toute écriture, l'est —
 *   · 400 : corps refusé par le contrôle de forme, rien n'a été lu ni écrit ;
 *   · 401 / 403 : session absente ou refusée, rien n'a été tenté ;
 *   · 409 : jeton périmé ou garde volumétrique, rien n'a été écrit ;
 *   · 503 : relecture impossible, rien n'a été écrit.
 * Tout le reste — coupure réseau (la requête a pu arriver), 500, réponse
 * illisible, délai dépassé — veut dire « je ne sais pas » : on n'affirme pas
 * « pas enregistrée ».
 */
// 400 (tour 3, point 4) : corps refusé par le contrôle de forme, AVANT toute
// lecture ou écriture (api/data.js).
const STATUTS_REFUS_CERTAIN = new Set([400, 401, 403, 409, 503]);
function refusCertain(e) {
  if (!e || e.illisible || e.delaiDepasse) return false;
  return STATUTS_REFUS_CERTAIN.has(e.status);
}

/**
 * Écrire le blob ET mettre l'écran à jour, dans le bon ordre.
 *
 *   · Geste de Dimitri (pas de `jeton`) : l'écran reçoit `d` AVANT l'écriture
 *     — comportement historique, sa saisie reste visible même refusée.
 *   · Écriture d'IMPORT (`jeton` fourni) : l'écran ne reçoit `d` qu'APRÈS la
 *     réussite. Avant, un import refusé en 409 avait déjà remplacé à l'écran
 *     la saisie de Dimitri qui venait de gagner ; le geste suivant la
 *     réécrasait en base, avec un jeton valide, sans aucun signal.
 *
 * @param {object} d
 * @param {{jeton?: string, adopterDonnees: Function}} p
 * @throws l'erreur de `saveDataRemote`, inchangée (avec son `status`).
 */
export async function ecrireDonnees(
  d,
  { jeton, adopterDonnees, retirerBandeau, montrerBandeau },
) {
  const pourImport = typeof jeton === "string";
  if (!pourImport) adopterDonnees(d);
  try {
    await saveDataRemote(d, pourImport ? { jeton } : {});
  } catch (e) {
    // Un geste refusé laisse sa saisie à l'écran : la prochaine relecture qui
    // remettra l'écran sur la base devra le dire (F13).
    // Un geste dont on NE SAIT PAS s'il est passé (réseau, 500, illisible,
    // délai) remplace le précédent : le dernier geste fait foi, et on ne dira
    // plus « pas enregistrée » (relecture adversariale F13 suite, tour 2).
    if (!pourImport) {
      saisieRefusee = refusCertain(e);
      echecIncertainDernier = !saisieRefusee;
      // Délai dépassé (tour 4, R1) : le serveur peut écrire APRÈS les 60 s du
      // navigateur — une relecture au même jeton ne prouverait rien. On reste
      // sur « je ne sais pas » : pas de repère, jamais « pas enregistrée ».
      // Tour 4 (R1 élargi) : sans réponse du serveur (coupure réseau) non
      // plus — la requête peut encore aboutir après la relecture. La
      // déduction « même jeton = pas passé » ne vaut que si le serveur a
      // RÉPONDU (500, réponse illisible).
      gesteIncertain = !saisieRefusee && !e?.delaiDepasse && !e?.sansDeduction;
    }
    throw e;
  }
  if (pourImport) {
    adopterDonnees(d);
    // L'import vient de remettre l'écran sur la base (+ iCal) : une saisie
    // refusée qui y restait vient de disparaître — on le dit (bouton iCal ;
    // le rafraîchissement automatique l'a déjà dit à sa relecture).
    direSaisieEffacee(montrerBandeau);
  } else {
    // Le geste est enregistré : l'écran montre la base, plus rien à signaler.
    saisieRefusee = false;
    gesteIncertain = false;
    if (typeof retirerBandeau === "function")
      retirerBandeau(MESSAGE_DATA_SAISIE_NON_ENREGISTREE);
  }
  return true;
}

/**
 * Les écritures d'un IMPORT (iCal automatique, bouton iCal) — protégées
 * contre l'enregistrement de Dimitri qui passerait pendant l'import.
 *
 * ══ LA COURSE (relectures F13, 2026-10-02) ══════════════════════════════════
 *
 * L'import part d'une relecture B0 et enregistre B0 + réservations iCal. S'il
 * enregistrait avec le jeton DÉTENU, et que Dimitri avait enregistré B1
 * pendant l'import, le jeton détenu serait déjà celui de B1 : le serveur
 * accepterait, et B1 serait écrasé en silence par B0 + iCal.
 *
 * Ici : chaque écriture de l'import porte le jeton des données dont elle part
 * (`jetonLu`, puis celui que rend chaque écriture de l'import), et juste
 * avant chacune, si le jeton détenu a changé depuis le départ (ou depuis la
 * dernière écriture de l'import), l'import s'ARRÊTE et le bandeau est montré.
 *
 * SAISIE (F13 suite, point 5) : `enSaisie`, si fourni, est relu juste avant
 * chaque écriture. Une saisie qui commence pendant l'import l'ARRÊTE, sans
 * bandeau (rien n'a bougé ailleurs) ; il reprendra au passage suivant.
 *
 * @param {{jetonLu: string, montrerBandeau: Function, enSaisie?: Function}} p
 */
export function preparerEcrituresImport({ jetonLu, montrerBandeau, enSaisie }) {
  let jetonImport = jetonLu || "";
  let detenuAttendu = jetonVersion;
  let arrete = false;
  const arreter = () => {
    if (!arrete) montrerBandeau(MESSAGE_DATA_CHANGEES_AILLEURS);
    arrete = true;
  };
  return {
    /** Le jeton à envoyer, ou `null` : l'import doit s'arrêter, rien n'est envoyé. */
    jetonPourEcrire() {
      if (arrete) return null;
      if (typeof enSaisie === "function" && enSaisie()) {
        arrete = true;
        return null;
      }
      if (jetonVersion !== detenuAttendu || ecrituresEnVol > 0) {
        arreter();
        return null;
      }
      return jetonImport;
    },
    /** Après l'écriture : succès → l'import repart du nouvel état ; échec → arrêt. */
    apresEcriture(ok) {
      if (ok) {
        jetonImport = jetonVersion;
        detenuAttendu = jetonVersion;
      } else {
        arrete = true;
      }
    },
    /** Le serveur a refusé en 409 : quelqu'un a écrit entre-temps. */
    conflit: arreter,
    arrete: () => arrete,
  };
}

/**
 * Écrit le blob de facturation.
 *
 * @throws {Error} le message du serveur — dont le conflit de version (409) et
 *   le refus des gardes volumétriques. Les deux sont affichés tels quels :
 *   ils disent ce qui s'est passé, que rien n'a été enregistré, et quoi faire.
 */
export async function saveDataRemote(data, opts = {}) {
  // Compté EN VOL jusqu'à ce que le jeton soit posé (ou l'échec levé) : une
  // relecture ou un import qui tombent dans cet intervalle s'abstiennent.
  ecrituresEnVol += 1;
  // ══ DÉLAI (F13, 2026-10-02) ═══════════════════════════════════════════════
  // Sans limite, une requête pendue gardait l'écriture « en vol » pour
  // toujours : plus aucune relecture adoptée, plus aucun import, et Dimitri
  // sans réponse. Au bout de 60 s : on ABANDONNE l'attente (la requête est
  // interrompue côté navigateur — le serveur, lui, a pu écrire : d'où « je ne
  // sais pas »), le compteur redescend, et une réponse tardive ne pose PAS son
  // jeton (`suivi.abandonnee`, lu juste avant la pose).
  const suivi = { abandonnee: false };
  const controleur =
    typeof AbortController === "function" ? new AbortController() : null;
  let minuteur = null;
  const envoi = envoyerData(data, opts, {
    suivi,
    signal: controleur ? controleur.signal : undefined,
  });
  // La promesse de l'envoi survit à l'abandon : son échec tardif (dont
  // l'interruption elle-même) est attendu et ne doit pas remonter comme une
  // erreur non gérée — il est noté en console, jamais tu.
  envoi.catch((e) => {
    if (suivi.abandonnee)
      console.warn(
        "[saveData] réponse arrivée après le délai, ignorée :",
        e?.message || e,
      );
  });
  const delai = new Promise((_, rejeter) => {
    minuteur = setTimeout(() => {
      suivi.abandonnee = true;
      if (controleur) controleur.abort();
      const e = new Error(MESSAGE_DATA_ECRITURE_DELAI);
      e.delaiDepasse = true;
      rejeter(e);
    }, DELAI_ECRITURE_MS);
  });
  try {
    return await Promise.race([envoi, delai]);
  } finally {
    clearTimeout(minuteur);
    ecrituresEnVol -= 1;
  }
}

async function envoyerData(data, { jeton } = {}, { suivi, signal } = {}) {
  // Un import enregistre avec le jeton des données DONT IL PART (cf.
  // `preparerEcrituresImport`), capturé ICI, avant toute attente : relu après
  // `authHeaders()`, le jeton détenu pourrait déjà être celui d'un
  // enregistrement de Dimitri passé entre-temps.
  const jetonEnvoye = typeof jeton === "string" ? jeton : jetonVersion;
  const entetes = await authHeaders();
  let res;
  try {
    res = await fetch("/api/data", {
      method: "POST",
      headers: { ...entetes, "x-data-version": jetonEnvoye },
      body: JSON.stringify(data || {}),
      ...(signal ? { signal } : {}),
    });
  } catch (e) {
    // F13 suite, point C (oui de Dimitri) : la requête n'a pas eu de réponse
    // (coupure réseau). Elle a pu arriver et l'écriture passer : on ne sait
    // pas. Le message brut du navigateur (« Failed to fetch ») ne dit rien
    // d'utile — on dit la vérité, et jamais « pas enregistrée ».
    const incertaine = new Error(MESSAGE_DATA_ECRITURE_DELAI);
    incertaine.incertaine = true;
    // Aucune réponse : la requête peut être encore en route (tour 4, R1).
    incertaine.sansDeduction = true;
    incertaine.cause = e;
    throw incertaine;
  }
  // 🔴 NE JAMAIS mémoriser le jeton avant d'avoir vérifié le succès.
  // Le rafraîchir sur un 409 rendrait un SECOND clic sur « Enregistrer »
  // gagnant — c'est-à-dire exactement l'écrasement silencieux que cette garde
  // existe pour empêcher. Sur conflit, la seule issue est de recharger.
  try {
    await parseOrThrow(
      res,
      "Enregistrement des données échoué",
      MESSAGE_DATA_ECRITURE_ILLISIBLE,
    );
  } catch (e) {
    // Le statut voyage avec l'erreur : un import distingue ainsi un conflit
    // (409, quelqu'un a écrit) d'une autre panne.
    // F13 suite, point C : une erreur serveur (5xx hors 503, qui est un refus
    // avant écriture) ne dit pas si l'écriture a eu lieu — « je ne sais pas ».
    // Les refus certains (401/403/409/503) gardent le message du serveur ; une
    // réponse illisible garde le sien, qui dit déjà « je ne sais pas ».
    if (!e.illisible && res.status >= 500 && res.status !== 503) {
      const incertaine = new Error(MESSAGE_DATA_ECRITURE_DELAI);
      incertaine.incertaine = true;
      incertaine.status = res.status;
      // Contrôle argent : « data_ecriture_echouee » N'EST PAS un refus
      // certain — setStorage rend aussi « échec » quand la liaison serveur →
      // base se coupe pendant l'envoi, alors que l'écriture a pu arriver. Le
      // serveur lui-même ne sait pas : pas de déduction « même jeton = pas
      // passé » (comme une coupure réseau), et son « PAS enregistrées »
      // n'est pas affiché.
      if (e.code === "data_ecriture_echouee") incertaine.sansDeduction = true;
      // Contrôle adversarial : un 502/504 vient de la PLATEFORME (fonction
      // coupée, délai), pas de l'appli — il ne dit rien de ce qu'elle a fait.
      // Seul un 500 lisible de l'appli autorise la déduction au même jeton.
      if (res.status !== 500) incertaine.sansDeduction = true;
      incertaine.cause = e;
      throw incertaine;
    }
    e.status = res.status;
    // Réponse illisible (page HTML de la plateforme, portail captif) : on ne
    // sait pas qui a répondu — aucune déduction (contrôle adversarial).
    if (e.illisible) e.sansDeduction = true;
    throw e;
  }
  // Délai dépassé avant cette réponse : l'appelant a déjà reçu « je ne sais
  // pas » ; adopter ce jeton maintenant le rendrait « à jour » sans que
  // l'écran le soit (cf. `appliquerRelecture`).
  if (suivi && suivi.abandonnee) return false;
  jetonVersion = res.headers?.get?.("x-data-version") || "";
  return true;
}

// Réservé aux tests : la version détenue est un état de module, elle doit
// pouvoir être posée et lue explicitement pour que les tests prouvent quelque
// chose plutôt que de constater un `undefined`.
export const _saisieRefusee = {
  lire: () => saisieRefusee,
  poser: (v) => {
    saisieRefusee = !!v;
  },
  lireIncertain: () => gesteIncertain,
  oublier: () => {
    saisieRefusee = false;
    gesteIncertain = false;
  },
};

export const _jeton = {
  lire: () => jetonVersion,
  poser: (v) => {
    jetonVersion = v || "";
  },
};
